import React, { useEffect, useState } from 'react';
import api from '../api';

const styles = {
  container: {
    minHeight: '100vh',
    padding: '40px 20px',
    backgroundColor: '#f7fafc',
    fontFamily: "'Inter', sans-serif",
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '32px',
    maxWidth: '1200px',
    margin: '0 auto 32px auto',
  },
  title: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#2d3748',
    margin: 0,
  },
  liveBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 16px',
    backgroundColor: '#e6fffa',
    color: '#319795',
    borderRadius: '20px',
    fontWeight: '600',
    fontSize: '14px',
    border: '1px solid #b2f5ea',
  },
  dot: {
    width: '8px',
    height: '8px',
    backgroundColor: '#38b2ac',
    borderRadius: '50%',
    animation: 'pulse 1.5s infinite',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '24px',
    maxWidth: '1200px',
    margin: '0 auto 40px auto',
  },
  statCard: {
    backgroundColor: 'white',
    padding: '24px',
    borderRadius: '16px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  statValue: {
    fontSize: '36px',
    fontWeight: '800',
    color: '#2b6cb0',
    marginBottom: '8px',
  },
  statLabel: {
    fontSize: '14px',
    color: '#718096',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  tableContainer: {
    maxWidth: '1200px',
    margin: '0 auto',
    backgroundColor: 'white',
    borderRadius: '16px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
    overflow: 'hidden',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    backgroundColor: '#edf2f7',
    padding: '16px 24px',
    textAlign: 'left',
    fontSize: '14px',
    fontWeight: '600',
    color: '#4a5568',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  td: {
    padding: '16px 24px',
    borderBottom: '1px solid #e2e8f0',
    fontSize: '16px',
    color: '#2d3748',
  },
  statusBadge: {
    padding: '6px 12px',
    borderRadius: '20px',
    fontSize: '13px',
    fontWeight: '600',
    display: 'inline-block',
  }
};

const getStatusStyle = (status) => {
  switch (status) {
    case 'Ongoing':
      return { backgroundColor: '#feebc8', color: '#c05621' };
    case 'Submitted':
      return { backgroundColor: '#c6f6d5', color: '#2f855a' };
    default:
      return { backgroundColor: '#e2e8f0', color: '#4a5568' };
  }
};

export default function Analytics() {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);

  const [password, setPassword] = useState(localStorage.getItem('adminPwd') || '');
  const [loggedIn, setLoggedIn] = useState(!!localStorage.getItem('adminPwd'));

  useEffect(() => {
    // Inject keyframes for pulse animation
    const styleSheet = document.createElement("style");
    styleSheet.type = "text/css";
    styleSheet.innerText = `
      @keyframes pulse {
        0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(56, 178, 172, 0.7); }
        70% { transform: scale(1); box-shadow: 0 0 0 10px rgba(56, 178, 172, 0); }
        100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(56, 178, 172, 0); }
      }
    `;
    document.head.appendChild(styleSheet);
    
    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  useEffect(() => {
    if (!loggedIn) return;
    fetchAnalytics();
    const interval = setInterval(fetchAnalytics, 500);
    return () => clearInterval(interval);
  }, [loggedIn]);

  const fetchAnalytics = async () => {
    try {
      const currentPwd = localStorage.getItem('adminPwd') || password;
      const { data } = await api.get(`/exam/analytics/live?pwd=${currentPwd}`);
      setStudents(prev => {
        const map = new Map(prev.map(s => [s.student_id, s]));
        data.forEach(s => map.set(s.student_id, s));
        const merged = Array.from(map.values());
        return merged.sort((a, b) => {
          if (a.status !== b.status) return a.status.localeCompare(b.status);
          return a.roll_no.localeCompare(b.roll_no);
        });
      });
    } catch (err) {
      if (err.response && err.response.status === 401) {
         setLoggedIn(false);
         localStorage.removeItem('adminPwd');
         // Only alert if they actually tried to login manually
         if (password) alert('Invalid Password');
      }
      console.error('Failed to fetch live analytics');
    } finally {
      setLoading(false);
    }
  };

  const totalLoggedIn = students.length;
  const totalOngoing = students.filter(s => s.status === 'Ongoing').length;
  const totalSubmitted = students.filter(s => s.status === 'Submitted').length;

  if (!loggedIn) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <h2>Admin Login</h2>
        <input 
          type="password" 
          value={password} 
          onChange={(e) => setPassword(e.target.value)} 
          placeholder="Enter password" 
          style={{ padding: '8px', marginBottom: '10px' }}
        />
        <button onClick={() => {
          localStorage.setItem('adminPwd', password);
          setLoggedIn(true);
        }} style={{ padding: '8px 16px' }}>Login</button>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Live Exam Analytics</h1>
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
          <button 
            onClick={async () => {
              if (window.confirm("Are you sure you want to clear all current student data? This will empty the dashboard until active students re-ping the server.")) {
                try {
                  await api.get(`/exam/clear-analytics?pwd=${password}`);
                  setStudents([]);
                } catch (err) {
                  alert("Failed to clear data.");
                }
              }
            }}
            style={{ padding: '8px 16px', backgroundColor: '#e2e8f0', color: '#4a5568', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}
          >
            Clear Old Data
          </button>
          <div style={styles.liveBadge}>
            <div style={styles.dot}></div>
            LIVE UPDATES
          </div>
        </div>
      </div>

      <div style={styles.statsGrid}>
        <div style={styles.statCard}>
          <div style={styles.statValue}>{totalLoggedIn}</div>
          <div style={styles.statLabel}>Active Users (All Time)</div>
        </div>
        <div style={styles.statCard}>
          <div style={{ ...styles.statValue, color: '#dd6b20' }}>{totalOngoing}</div>
          <div style={styles.statLabel}>Currently Writing</div>
        </div>
        <div style={styles.statCard}>
          <div style={{ ...styles.statValue, color: '#38a169' }}>{totalSubmitted}</div>
          <div style={styles.statLabel}>Exams Submitted</div>
        </div>
      </div>

      <div style={styles.tableContainer}>
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#718096' }}>Loading live data...</div>
        ) : students.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#718096' }}>No active students at the moment.</div>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Roll Number</th>
                <th style={styles.th}>Name</th>
                <th style={styles.th}>Logged In</th>
                <th style={styles.th}>Status</th>
              </tr>
            </thead>
            <tbody>
              {students.map(student => (
                <tr key={student.student_id}>
                  <td style={{ ...styles.td, fontWeight: '600' }}>{student.roll_no}</td>
                  <td style={styles.td}>{student.name}</td>
                  <td style={styles.td}>Yes</td>
                  <td style={styles.td}>
                    <span style={{ ...styles.statusBadge, ...getStatusStyle(student.status) }}>
                      {student.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
